import { useEffect, useMemo } from "react"

import { formatNumber } from "accounting"
import dayjs from "dayjs"
import { useSelector } from "react-redux"

import "chartjs-adapter-moment"

import {
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import { Line } from "react-chartjs-2"

import EmptyComponent from "../../../../Components/Empty"
import { INCOGNITO_MASK } from "../../../../Components/Private"
import { useIncognito } from "../../../../hooks/useIncognito"
import { isIncognitoActive } from "../../../../Utils/incognito"
import { formatCurrency } from "../../../../Utils/utils"
import { baseOptions } from "../../../DashboardPage/components/chartsOptions"
import { tooltipLineWithPoints } from "../../../DashboardPage/components/chartsPlagins"

import styles from "./trendsLineChart.module.less"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)
ChartJS.defaults.font.family = "Inter, sans-serif"

// The multi-line trend chart, shared by the "Category trends over time" card and the
// categories / reason-codes breakdown page, so the two always plot the same way: same
// colours per series index, same dashed hover line with a dot on every series, same dark
// HTML tooltip. Two copies of this drifted apart within a week the last time.

// One colour per series, in the order the category donut lists its slices, so a category
// is the same colour on the donut, the card and the breakdown page. Reason-code series
// reuse the same ramp (there are more codes than colours, so it cycles).
export const LINE_COLORS = [
  "#225AEA",
  "#661AC6",
  "#16895A",
  "#DF7009",
  "#D71313",
  "#0E7490",
  "#7C3AED",
  "#B45309"
]

// The shared dark HTML tooltip our other charts use (Prevent overview, Health overview):
// a #27272A box with a muted date heading and one row per series, the label on the left
// with its colour dot and the number floated right. `tooltipId` keeps each chart's element
// separate so two charts on one page cannot fight over it (the page removes its element on
// unmount). `unit` picks the date granularity, `metric` the number format.
const trendsTooltip = (unit, metric, tooltipId, series) => (context) => {
  let el = document.getElementById(tooltipId)
  if (!el) {
    el = document.createElement("div")
    el.id = tooltipId
    document.body.appendChild(el)
  }
  const model = context.tooltip
  if (model.opacity === 0) {
    el.style.opacity = 0
    return
  }
  const points = model.dataPoints || []
  const dateStr = dayjs(points[0]?.label).format(unit === "month" ? "MMM YYYY" : "DD MMM YYYY")
  const index = points[0]?.dataIndex
  const rows = series
    .map(
      (s, i) =>
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;padding-top:8px">' +
        '<span style="display:flex;align-items:center">' +
        `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${s.color || LINE_COLORS[i % LINE_COLORS.length]};margin-right:8px"></span>` +
        `${s.label}</span>` +
        `<span style="font-weight:500">${
          isIncognitoActive()
            ? INCOGNITO_MASK
            : metric === "value"
              ? formatCurrency(s.data[index] ?? 0, true)
              : formatNumber(s.data[index] ?? 0, 0, ",")
        }</span></div>`
    )
    .join("")
  el.innerHTML = `<div style="font-weight:500;color:#7A7A83">${dateStr}</div>${rows}`
  // Glide between points while already visible (fade in place on first show, see chartsOptions).
  const wasVisible = parseFloat(el.style.opacity || 0) > 0
  Object.assign(el.style, {
    opacity: 1,
    transition: wasVisible
      ? "left 0.13s cubic-bezier(0.22, 1, 0.36, 1), top 0.13s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.12s ease"
      : "opacity 0.12s ease",
    position: "absolute",
    zIndex: "1010",
    background: "#27272A",
    borderRadius: "12px",
    padding: "12px",
    color: "#FFFFFF",
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    minWidth: "180px",
    pointerEvents: "none"
  })
  const pos = context.chart.canvas.getBoundingClientRect()
  const w = el.offsetWidth
  let deltaLeft = w / 2
  if (pos.width - model.caretX <= w / 2) deltaLeft = w * 0.75
  else if (model.caretX < w / 2) deltaLeft = w * 0.25
  el.style.left = pos.left - deltaLeft + window.scrollX + model.caretX + "px"
  el.style.top = pos.top + window.scrollY + model.caretY - el.offsetHeight - 16 + "px"
}

// `series` is [{ key, label, data }], picked by the caller out of category-trends/ (its count
// or its amount). Labels are already translated by the caller: it knows whether a series
// is a category name (translatable) or a reason code (not).
const TrendsLineChart = ({
  labels = [],
  unit = "day",
  series = [],
  metric = "count",
  tooltipId
}) => {
  const theme = useSelector((s) => s.nav.theme)
  const dark = theme === "dark"
  const incognito = useIncognito()

  // The tooltip element lives on <body>, outside React, so the chart that creates it removes it.
  useEffect(() => () => document.getElementById(tooltipId)?.remove(), [tooltipId])

  const hasData = labels.length > 0 && series.some((s) => s.data.some((v) => v > 0))
  const plotted = series.filter((s) => s.plotted !== false)

  // Clean lines, like our other line charts: baseOptions sets pointRadius 0 +
  // lineTension 0.25 for line datasets, and tooltipLineWithPoints paints the hover dots,
  // so no per-point markers here.
  const data = {
    labels,
    datasets: plotted.map((s, i) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color || LINE_COLORS[i % LINE_COLORS.length],
      backgroundColor: s.color || LINE_COLORS[i % LINE_COLORS.length],
      borderWidth: 2,
      fill: false,
      spanGaps: true,
      yAxisID: "y"
    }))
  }

  const options = useMemo(() => {
    const base = baseOptions(theme)
    return {
      ...base,
      interaction: { mode: "index", intersect: false },
      scales: {
        ...base.scales,
        x: { ...base.scales.x, time: { ...base.scales.x.time, unit } }
      },
      plugins: {
        "vertical-line-plugin-with-points": { theme },
        title: { display: false },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
            color: dark ? "#FCFCFC" : "#27272A",
            font: { size: 14, family: "Inter, sans-serif" }
          }
        },
        // The shared dark HTML tooltip (enabled:false + external), matching the
        // Prevent/Health line charts instead of chart.js's built-in box.
        tooltip: {
          enabled: false,
          mode: "index",
          intersect: false,
          external: trendsTooltip(unit, metric, tooltipId, series)
        }
      }
    }
  }, [theme, dark, unit, metric, tooltipId, series])

  if (!hasData) return <EmptyComponent type="no_data_available" />

  return (
    <div className={styles.chart}>
      <Line
        key={`${theme}-${incognito}`}
        data={data}
        options={options}
        plugins={[tooltipLineWithPoints]}
      />
    </div>
  )
}

export default TrendsLineChart
